import React, { Fragment, useCallback, useMemo, useRef } from 'react';

import { CONNECTOR_OAUTH_STATUS } from '@confluence/connectors/entry-points/connector-oauth-types.ts';
import { ThirdPartyRelatedContentPromoCard } from '@confluence/experiment-related-content-connections/entry-points/ThirdPartyRelatedContentPromoCard.ts';
import { ThirdPartyRelatedContentPromoCardSkeleton } from '@confluence/experiment-related-content-connections/entry-points/ThirdPartyRelatedContentPromoCardSkeleton.ts';
import { readConnectorRowsFromLocalStorage } from '@confluence/experiment-related-content-connections/entry-points/readConnectorRows.ts';
import type { QualifyingProduct } from '@confluence/experiment-related-content-connections/entry-points/thirdPartyPromptTypes.ts';
import { useThirdPartyConnectorOAuth } from '@confluence/experiment-related-content-connections/entry-points/useThirdPartyConnectorOAuth.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useThirdPartyPromptProduct } from './hooks/useThirdPartyPromptProduct.ts';
import { useThirdPartyPromptState } from './hooks/useThirdPartyPromptState.ts';

/**
 * Mounted once the product is known. `status` is the outcome, not promise settlement: `connect`
 * resolves on a cancelled popup too, so awaiting it would report success when the reader closed the
 * window.
 */
const ConnectedPromptCard = ({
	product,
	children,
}: {
	product: QualifyingProduct;
	children: React.ReactNode;
}) => {
	const { cloudId, userId } = useSessionData();

	const connectorRows = useMemo(
		() => readConnectorRowsFromLocalStorage({ userId: userId ?? '', cloudId }).rows,
		[cloudId, userId],
	);

	const { status, connect } = useThirdPartyConnectorOAuth({
		appId: product.appId,
		connectorRows,
		cloudId,
		userId,
	});

	const startConnect = useCallback(() => {
		void connect();
	}, [connect]);

	// Latch the treatment decision before connect flips `status`. After connect the prompt stays
	// mounted so its success flag can fire. Already-connected first paint never reaches `expValEquals`.
	const keepPrompt = useRef(false);
	if (status !== CONNECTOR_OAUTH_STATUS.CONNECTED) {
		keepPrompt.current = expValEquals('confluence_3p_in_related_content', 'isEnabled', true);
	}

	if (!keepPrompt.current) {
		return <Fragment>{children}</Fragment>;
	}

	return (
		<Fragment>
			<ThirdPartyRelatedContentPromoCard
				product={product}
				status={status}
				onConnect={startConnect}
				showCardAsListitem={false}
			/>
			{status === CONNECTOR_OAUTH_STATUS.CONNECTED ? children : null}
		</Fragment>
	);
};

/**
 * The lowest-ranked grid slot. It shows the skeleton whenever a product qualifies, then swaps to the
 * prompt once the page names one. `children` is the first-party card the prompt would replace, and it
 * comes back when nothing qualifies, when the document names none of the candidates, or after a
 * successful connect.
 */
export const ThirdPartyPromptSlot = ({
	entityId,
	children,
}: {
	entityId: string;
	children: React.ReactNode;
}): React.JSX.Element => {
	const { state } = useThirdPartyPromptState(entityId);
	const { product, isResolved } = useThirdPartyPromptProduct(state);

	if (state.kind === 'none') {
		return <Fragment>{children}</Fragment>;
	}

	if (!isResolved) {
		return <ThirdPartyRelatedContentPromoCardSkeleton />;
	}

	return product ? (
		<ConnectedPromptCard product={product}>{children}</ConnectedPromptCard>
	) : (
		<Fragment>{children}</Fragment>
	);
};
